/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dateFixer from '../../helpers/dateFixer';
import MerchantOrder, { MerchantOrderStatus } from '../../interfaces/MerchantOrder';
import { RemoteData, RemoteError, RemoteStatus } from '../SharedTypes';
import { cancelOrder, fetchOrder, fetchOrderStatus } from './OrderThunks';

type OrderState = {
	order: RemoteData<MerchantOrder>;
	paidAmount: string;
	cancel: RemoteData<void>;
};

const initialState: OrderState = {
	order: {
		status: RemoteStatus.None,
	},
	paidAmount: '0',
	cancel: { status: RemoteStatus.None },
};

const slice = createSlice({
	initialState,
	name: 'order',
	reducers: {
		reset: () => initialState,
		setOrder: (state, { payload }: PayloadAction<MerchantOrder>) => {
			state.order.status = RemoteStatus.Done;
			state.order.data = payload;
			state.order.data.created = dateFixer(state.order.data.created);
			state.order.data.validUntil = dateFixer(state.order.data.validUntil);
		},
		setOrderStatus: (state, { payload }: PayloadAction<MerchantOrderStatus>) => {
			if (state.order.data) state.order.data.status = payload;
		},
	},
	extraReducers: (builder) =>
		builder
			.addCase(fetchOrder.pending, (state) => {
				state.order.status = RemoteStatus.InProgress;
				delete state.order.error;
			})
			.addCase(fetchOrder.rejected, (state, { payload }) => {
				state.order.status = RemoteStatus.Error;
				state.order.error = payload as RemoteError;
			})
			.addCase(fetchOrder.fulfilled, (state, { payload }) => {
				state.order.status = RemoteStatus.Done;
				state.order.data = payload;
				state.order.data.created = dateFixer(state.order.data.created);
				state.order.data.validUntil = dateFixer(state.order.data.validUntil);
			})
			.addCase(fetchOrderStatus.fulfilled, (state, { payload }) => {
				if (state.order.data) state.order.data.status = payload.status;
				if (payload.paidAmount) state.paidAmount = payload.paidAmount;
			})
			.addCase(cancelOrder.pending, (state) => {
				state.cancel.status = RemoteStatus.InProgress;
				delete state.cancel.error;
			})
			.addCase(cancelOrder.rejected, (state, { payload }) => {
				state.cancel.status = RemoteStatus.Error;
				state.cancel.error = payload as RemoteError;
			})
			.addCase(cancelOrder.fulfilled, (state) => {
				state.cancel.status = RemoteStatus.Done;
			}),
});

export default slice;
